import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DoorClosed, Radio, CalendarClock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AppHeader, UserMenu } from '@/components/layout/AppHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import StudentInterface from './StudentInterface';
import ReviewStudentInterface from './ReviewStudentInterface';
import StudentAuth from './StudentAuth';
import { getStudentToken, clearStudentToken } from '../authCookie';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ClassJoinInfo {
  class_id: number;
  class_code: string;
  join_code: string;
  class_name: string;
  class_period: string;
}

interface ActiveSessionSummary {
  session_id: string;
  mode: string;
  session_timecreated: string;
  expires_at?: string | null;
}

function sessionTitle(s: ActiveSessionSummary): string {
  return s.mode === 'review' ? 'Review session' : 'Live lecture';
}

function sessionSubtitle(s: ActiveSessionSummary): string {
  if (s.mode === 'review' && s.expires_at) {
    const until = new Date(s.expires_at).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `Open until ${until}`;
  }
  const started = new Date(s.session_timecreated).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Started ${started}`;
}

export default function StudentPage() {
  const { sessionId: joinCode } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [classInfo, setClassInfo] = useState<ClassJoinInfo | null>(null);
  const [sessions, setSessions] = useState<ActiveSessionSummary[]>([]);
  const [selected, setSelected] = useState<ActiveSessionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [studentEmail, setStudentEmail] = useState<string | null>(null);
  const [studentId, setStudentId] = useState<string | null>(null);
  const [authCheckDone, setAuthCheckDone] = useState(false);

  // Restore student session from cookie
  useEffect(() => {
    if (studentEmail) {
      setAuthCheckDone(true);
      return;
    }
    const token = getStudentToken();
    if (!token) {
      setAuthCheckDone(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${backendUrl}/api/auth/student/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          setStudentEmail(data.student_email);
          setStudentId(data.student_id);
        } else {
          clearStudentToken();
        }
      } catch {
        if (!cancelled) clearStudentToken();
      } finally {
        if (!cancelled) setAuthCheckDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [studentEmail]);

  useEffect(() => {
    if (!studentEmail) return;

    const code = (joinCode || '').trim().toUpperCase();
    if (!code) {
      setError('No join code provided');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const studentToken = getStudentToken();
        const response = await fetch(
          `${backendUrl}/api/classes/join/${encodeURIComponent(code)}/active-sessions`,
          { headers: studentToken ? { Authorization: `Bearer ${studentToken}` } : {} }
        );
        const data = await response.json();
        if (cancelled) return;

        if (!response.ok) {
          setError(data?.detail || 'Invalid join code.');
          return;
        }

        setClassInfo({
          class_id: data.class_id,
          class_code: data.class_code,
          join_code: data.join_code,
          class_name: data.class_name,
          class_period: data.class_period,
        });
        setSessions(data.sessions);
        // With a single open session, skip the picker and go straight in.
        if (data.sessions.length === 1) {
          setSelected(data.sessions[0]);
        }
      } catch (err) {
        console.error('Error fetching active sessions:', err);
        if (!cancelled) setError('Unable to connect to server. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [joinCode, studentEmail]);

  const handleLogout = () => {
    clearStudentToken();
    setStudentEmail(null);
    setStudentId(null);
  };

  if (!studentEmail) {
    return <StudentAuth backendUrl={backendUrl} onAuthenticated={(s) => { setStudentEmail(s.student_email); setStudentId(s.student_id); }} />;
  }

  const header = (
    <AppHeader
      left={
        classInfo && (
          <div className="hidden min-w-0 items-center gap-3 text-sm sm:flex">
            <span className="h-5 w-px bg-border" aria-hidden />
            <span className="font-semibold">{classInfo.class_code}</span>
            <span className="truncate text-muted-foreground">{classInfo.class_name}</span>
          </div>
        )
      }
      right={<UserMenu name={studentEmail} onLogout={handleLogout} />}
    />
  );

  if (!authCheckDone || isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        {header}
        <main className="container max-w-3xl py-10">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-3 h-4 w-40" />
          <Skeleton className="mt-10 h-48 w-full rounded-xl" />
        </main>
      </div>
    );
  }

  // No code match, or the class exists but nothing is open right now.
  if (error || sessions.length === 0) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        {header}
        <main className="container max-w-md py-16">
          <div className="rounded-2xl border bg-card shadow-soft">
            <EmptyState
              icon={<DoorClosed />}
              title="This class isn't open right now"
              description={error || 'The code is invalid or your professor hasn\'t started a session yet. Use the same link once class begins.'}
            />
            <div className="border-t p-4">
              <Button variant="outline" className="w-full" onClick={() => navigate('/join')}>
                Try another code
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // More than one session is open: let the student choose which to join.
  if (!selected) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        {header}
        <main className="container max-w-md py-16">
          <h1 className="text-2xl font-bold tracking-tight">Choose a session</h1>
          <p className="mt-2 text-muted-foreground">
            {classInfo?.class_code} has more than one session open. Pick the one you want to join.
          </p>
          <ul className="mt-8 space-y-3">
            {sessions.map((s) => (
              <li key={s.session_id}>
                <button
                  type="button"
                  onClick={() => setSelected(s)}
                  className="flex w-full items-center gap-4 rounded-2xl border bg-card p-4 text-left shadow-soft transition-colors hover:bg-muted"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    {s.mode === 'review' ? <CalendarClock aria-hidden /> : <Radio aria-hidden />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{sessionTitle(s)}</span>
                    <span className="block truncate text-sm text-muted-foreground">{sessionSubtitle(s)}</span>
                  </span>
                  <ChevronRight className="shrink-0 text-muted-foreground" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {header}
      {selected.mode === 'review' ? (
        <ReviewStudentInterface
          sessionId={selected.session_id}
          classCode={classInfo?.class_code || ''}
          expiresAt={selected.expires_at}
          backendUrl={backendUrl}
        />
      ) : (
        <StudentInterface
          sessionId={selected.session_id}
          classCode={classInfo?.class_code || ''}
          className={classInfo?.class_name || ''}
          sessionTimecreated={selected.session_timecreated}
          backendUrl={backendUrl}
          studentId={studentId || undefined}
        />
      )}
    </div>
  );
}
